/**
 * Utility for adding behaviours to buttons that show / hide content.
 * It currently assumes there is a transition, and that it occurs on
 * the element that gets the targetActiveClasses
 */

export class A11yToggle {
  /**
   * Constructor
   * @param { import("./A11yToggle").A11yToggleOptions } options - Intialisation options
   */
  constructor(options) {
    this.triggerEl = options.triggerEl;
    this.targetEl = options.targetEl;
    this.textEl = options.textEl;
    this.openText = options.openText || 'Open';
    this.closeText = options.closeText || 'Close';

    this.triggerClasses = new Set(['a11y-toggle-trigger']);
    this.targetClasses = new Set(['a11y-toggle-target']);

    // Extend trigger/target classses with any that come in
    options.triggerClasses?.forEach((className) => {
      this.triggerClasses.add(className);
    });
    options.targetClasses?.forEach((className) => {
      this.targetClasses.add(className);
    });

    // User defined active classes
    this.triggerActiveClasses = new Set(options.triggerActiveClasses);
    this.targetActiveClasses = new Set(options.targetActiveClasses);

    // Action whenever button is clicked
    this.onToggle = options.onToggle;

    // Actions before the transtion, such as showing hidden content
    this.beforeShow = options.beforeShow;
    this.beforeHide = options.beforeHide;

    // Actions after the transtion completes, such as hiding content from keyboard focus
    this.afterShow = options.afterShow;
    this.afterHide = options.afterHide;

    // Initialise if trigger and target provided
    this.triggerEl && this.targetEl ? this.initialiseToggle() : null;
  }

  /**
   * Set defaults
   */
  initialiseToggle() {
    // Assign default textEl if this didn't get picked up in the constructor
    this.textEl = this.textEl || this.triggerEl;

    // Add default classes
    this.triggerEl.classList.add(...this.triggerClasses);
    this.targetEl.classList.add(...this.targetClasses);

    // Set default state
    const initialState = this.triggerEl.getAttribute('aria-expanded');
    if (initialState === 'true') {
      this.currentState = true;
      this.beforeShow?.();
    } else {
      this.triggerEl.setAttribute('aria-expanded', 'false');
      this.currentState = false;
      this.afterHide?.();
    }

    // Fill in the controls attr if it's not already
    if (this.triggerEl.getAttribute('aria-controls') === null) {
      this.triggerEl.setAttribute('aria-controls', this.targetEl.id);
    }

    this.#bindToggle();
  }

  /**
   * Setup event listeners and perform other actions
   */
  #bindToggle() {
    this.triggerEl.addEventListener('click', () => {
      this.currentState = !this.currentState;

      this.#updateAria();
      this.#updateText();
      this.#updateClasses();
      this.onToggle?.(this.currentState);
    });

    // Use transition API hooks
    this.targetEl.addEventListener('transitionend', (e) => {
      // Only transitons on target
      if (e.target != this.targetEl) return;

      if (this.currentState) {
        this.afterShow?.(this);
      } else {
        this.afterHide?.(this);
      }
    });

    this.targetEl.addEventListener('transitionstart', (e) => {
      // Only transitons on target
      if (e.target != this.targetEl) return;

      if (this.currentState) {
        this.beforeShow?.(this);
      } else {
        this.beforeHide?.(this);
      }
    });
  }

  /**
   * Make any necessary updates to ARIA attributes
   */
  #updateAria() {
    this.triggerEl.setAttribute('aria-expanded', this.currentState);
  }

  /**
   * Make any necessary updates to ARIA attributes
   */
  #updateText() {
    this.textEl.innerText = this.currentState ? this.closeText : this.openText;
  }

  /**
   * Update the classes on the target and the trigger
   */
  #updateClasses() {
    this.triggerActiveClasses.size
      ? this.triggerEl.classList.toggle(...this.triggerActiveClasses)
      : null;
    this.targetActiveClasses.size
      ? this.targetEl.classList.toggle(...this.targetActiveClasses)
      : null;
  }
}
